import React, { PureComponent } from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { Button } from '@eb/eds-button';
import { TYPE_LINK } from '@eb/eds-button';

import TabNavigation from './_internal/TabNavigation';
import StickyHeader from './_internal/StickyHeader';
import FeatureComparisonTable from './_internal/FeatureComparisonTable';

import { DEFAULT_PACKAGE_TYPES, DEFAULT_PACKAGES } from '../constants/main';
import { ASSORTMENTS_FORMATTED_PRICING_PROP_TYPE } from '../constants/pricingPropTypes';
import { FEATURES_COMPARISON_PROP_TYPES } from '../constants/componentPropTypes';

import './featuresComparison.scss';
/*
Plan type (e.g. Essentials) heading cell
*/
const PlanHeadingCell = ({
    packageName,
    learnMoreAction,
    getStartedAction,
    isStickyHeader,
    featureDescription,
    hasFeatureDescriptions,
    CTA,
    CTAStyle,
}) => {
    const planHeadingCellClassName = classNames(
        'eds-text--center',
        'eds-text-color--grey-700',
        'eds-g-cell',
        'eds-g-cell-2-12',
        'eds-l-pad-vert-3',
        'eds-g-cell--has-overflow',
        {
            'feature-comparison__plan-header-content': hasFeatureDescriptions,
        },
    );

    const planHeadingButtonClassName = classNames('eds-l-pad-vert-3', {
        'feature-comparison__plan-header-btn-wrapper': hasFeatureDescriptions,
    });
    let ctaButton = null;

    if (!isStickyHeader) {
        const style = CTAStyle || TYPE_LINK;

        ctaButton = (
            <div className={planHeadingButtonClassName}>
                <Button
                    style={style}
                    disabled={!CTAStyle}
                    onClick={getStartedAction}
                    __containerClassName="eds-text--center feature-comparison__plan-header-btn"
                >
                    {CTA}
                </Button>
            </div>
        );
    }

    return (
        <div className={planHeadingCellClassName} role="columnheader">
            <p className="eds-text-bl eds-text-weight--heavy">
                {learnMoreAction ? (
                    <Button style="anchor" onClick={learnMoreAction}>
                        {packageName}
                    </Button>
                ) : (
                    packageName
                )}
            </p>
            <p className="eds-text-color--grey-600 eds-l-pad-all-1 eds-l-mar-hor-3">
                {featureDescription}
            </p>
            {ctaButton}
        </div>
    );
};

const PlanHeading = ({ primaryHeading, secondaryHeading }) => {
    const planHeadingClasses = classNames(
        'eds-g-cell eds-g-cell-12-12 eds-g-offset-0-12 eds-g-cell-sw-8-12 eds-g-cell-mn-6-12 eds-g-offset-mw-1-12 eds-g-cell-mw-4-12',
        'eds-l-pad-hor-6 eds-l-mw-pad-left-0 eds-l-mw-pad-right-12 eds-l-lg-pad-left-0 eds-l-lg-pad-right-12 eds-l-lw-pad-left-0 eds-l-lw-pad-right-12 eds-l-pad-top-6',
    );

    return (
        <div className={planHeadingClasses}>
            <h1 className="eds-text-hm eds-text-color--grey-900">
                {primaryHeading}
            </h1>
            <h2 className="eds-text-bl eds-text-color--grey-600 eds-l-mar-top-6">
                {secondaryHeading}
            </h2>
        </div>
    );
};

/*
Plan type (e.g. Essentials) header
*/
const PlanHeader = ({
    primaryHeading,
    secondaryHeading,
    packages,
    getStartedActions,
    learnMoreActions,
    isStickyHeader,
    featureDescriptions = {},
    hasFeatureDescriptions,
    CTAs,
    CTAStyles,
    packagesTotal,
}) => {
    const planHeaderContent = packages.map((packageName, index) => (
        <PlanHeadingCell
            key={packageName}
            packageName={packageName}
            learnMoreAction={learnMoreActions && learnMoreActions[index]}
            getStartedAction={getStartedActions[index]}
            isStickyHeader={isStickyHeader}
            featureDescription={featureDescriptions[index]}
            hasFeatureDescriptions={hasFeatureDescriptions}
            CTA={CTAs[index]}
            CTAStyle={CTAStyles[index]}
            isPrimary={!index}
            packagesTotal={packagesTotal}
        />
    ));

    const planHeadingsClassName = classNames(
        'marketing-container__max-width',
        'eds-g-grid',
        'eds-l-mn-pad-hor-6',
        'eds-l-md-pad-hor-6',
        {
            'feature-comparison__plan-header': hasFeatureDescriptions,
        },
    );

    return (
        <div className={planHeadingsClassName} role="row">
            <PlanHeading
                primaryHeading={primaryHeading}
                secondaryHeading={secondaryHeading}
            />
            {planHeaderContent}
        </div>
    );
};

export default class FeaturesComparison extends PureComponent {
    static propTypes = {
        ...ASSORTMENTS_FORMATTED_PRICING_PROP_TYPE,
        ...FEATURES_COMPARISON_PROP_TYPES,
        /*
        Show Annual Fee row (Beyond Free Smokescreen)
        */
        shouldShowAnnualFee: PropTypes.bool,
    };

    static defaultProps = {
        packages: DEFAULT_PACKAGES,
        packageTypes: DEFAULT_PACKAGE_TYPES,
    };

    state = {
        node: null,
        selectedIndex: 1,
    };

    UNSAFE_componentWillMount() {
        const { packageTypes, selectedPackage } = this.props;
        const selectedIndex = packageTypes.indexOf(selectedPackage);

        if (selectedIndex > -1) {
            this.setState({
                selectedIndex,
            });
        }
    }

    componentWillUnmount() {
        this.props.clearSelectedPackage();
    }

    /*
    Helper function to set a ref for a sticky component's parent element
    (the element that needs to be scrolled past to trigger a stick)
    */
    _setRef(node) {
        this.setState({
            node,
        });
    }

    /*
    Helper function to set a given package index to state,
    allowing the component to render the appropriate package content
    */
    _handleHeaderTabClick(selectedIndex) {
        this.setState({
            selectedIndex,
        });
    }

    /*
    Helper function to generate _handleHeaderTabClick actions for each
    heading tab that routes to the appropriate content for a given index
    */
    _getTabActions() {
        const { packages } = this.props;
        const tabActions = packages.map((packageName, index) =>
            this._handleHeaderTabClick.bind(this, index),
        );

        return tabActions;
    }

    render() {
        const {
            locale,
            packages,
            packageTypes,
            feeBreakdown,
            assortmentsTax,
            primaryHeading,
            secondaryHeading,
            featureDescriptions,
            paymentProcessingFeature,
            sections,
            getStartedActions,
            CTAs,
            CTAStyles,
            isSimplePricing,
            extraMainFeatures,
            pricePerPaidTicket,
            pricePerFreeTicket,
            shouldShowAnnualFee,
            showMaxFee,
            learnMoreActions,
            shouldShowCappedFee,
            useRefactoredPricingTableComparison,
        } = this.props;
        const { node, selectedIndex } = this.state;
        const tabActions = this._getTabActions();
        const hasFeatureDescriptions = !_.isEmpty(featureDescriptions);

        let smallScreenCTA = null;
        const proPlusInTabs = packages.length >= 4;

        if (isSimplePricing) {
            smallScreenCTA = (
                <div className="eds-l-pad-hor-6 eds-text--center eds-text-color--grey-600 eds-l-pad-bot-4">
                    <p className="eds-l-pad-vert-4">
                        {featureDescriptions[selectedIndex]}
                    </p>
                    <div className="eds-g-cell-sm-5-12 eds-align--center">
                        <Button
                            style={CTAStyles[selectedIndex]}
                            size="block"
                            // eslint-disable-next-line react/jsx-handler-names
                            onClick={getStartedActions[selectedIndex]}
                        >
                            {CTAs[selectedIndex]}
                        </Button>
                    </div>
                    {learnMoreActions && learnMoreActions[selectedIndex] && (
                        <Button
                            style="anchor"
                            // eslint-disable-next-line react/jsx-handler-names
                            onClick={learnMoreActions[selectedIndex]}
                            __containerClassName="eds-text-bm eds-text-weight--regular eds-l-pad-top-4"
                        >
                            Learn more
                        </Button>
                    )}
                </div>
            );
        }

        return (
            <div className="eds-bg-color--white" ref={this._setRef.bind(this)}>
                <div className="eds-show-up-md eds-l-pad-bot-6">
                    <div className="eds-l-pad-top-8 eds-l-pad-bot-6 eds-bg-color--grey-100">
                        <PlanHeader
                            primaryHeading={primaryHeading}
                            secondaryHeading={secondaryHeading}
                            packages={packages}
                            featureDescriptions={featureDescriptions}
                            hasFeatureDescriptions={hasFeatureDescriptions}
                            getStartedActions={getStartedActions}
                            learnMoreActions={learnMoreActions}
                            CTAs={CTAs}
                            CTAStyles={CTAStyles}
                            packagesTotal={packages.length}
                        />
                    </div>
                    <StickyHeader parentNode={node}>
                        <PlanHeader
                            packages={packages}
                            CTAs={CTAs}
                            CTAStyles={CTAStyles}
                            getStartedActions={getStartedActions}
                            learnMoreActions={learnMoreActions}
                            isStickyHeader={true}
                        />
                    </StickyHeader>
                    <div className="eds-l-pad-top-6">
                        <FeatureComparisonTable
                            packageTypes={packageTypes}
                            packagesTotal={packages.length}
                            feeBreakdown={feeBreakdown}
                            assortmentsTax={assortmentsTax}
                            paymentProcessingFeature={paymentProcessingFeature}
                            extraMainFeatures={extraMainFeatures}
                            isSimplePricing={isSimplePricing}
                            showMaxFee={showMaxFee}
                            sections={sections}
                            pricePerPaidTicket={pricePerPaidTicket}
                            pricePerFreeTicket={pricePerFreeTicket}
                            shouldShowAnnualFee={shouldShowAnnualFee}
                            shouldShowCappedFee={shouldShowCappedFee}
                            locale={locale}
                            useRefactoredPricingTableComparison={
                                useRefactoredPricingTableComparison
                            }
                        />
                    </div>
                </div>
                <div className="eds-show-down-mn eds-text--center">
                    <div className="eds-l-mar-bot-12">
                        <PlanHeading
                            primaryHeading={primaryHeading}
                            secondaryHeading={secondaryHeading}
                        />
                    </div>
                    <TabNavigation
                        currentIndex={selectedIndex}
                        tabs={packages}
                        tabActions={tabActions}
                        isSmallType={proPlusInTabs}
                    />
                    <StickyHeader parentNode={node}>
                        <TabNavigation
                            currentIndex={selectedIndex}
                            tabs={packages}
                            tabActions={tabActions}
                            isSmallType={proPlusInTabs}
                        />
                    </StickyHeader>
                    {smallScreenCTA}
                    <FeatureComparisonTable
                        packageTypes={packageTypes}
                        packagesTotal={packages.length}
                        feeBreakdown={feeBreakdown}
                        assortmentsTax={assortmentsTax}
                        paymentProcessingFeature={paymentProcessingFeature}
                        sections={sections}
                        isSimplePricing={isSimplePricing}
                        showMaxFee={showMaxFee}
                        extraMainFeatures={extraMainFeatures}
                        planIndex={selectedIndex}
                        pricePerPaidTicket={pricePerPaidTicket}
                        pricePerFreeTicket={pricePerFreeTicket}
                        shouldShowAnnualFee={shouldShowAnnualFee}
                        shouldShowCappedFee={shouldShowCappedFee}
                        locale={locale}
                        useRefactoredPricingTableComparison={
                            useRefactoredPricingTableComparison
                        }
                    />
                </div>
            </div>
        );
    }
}
